'use client';

import { useEffect, useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Mail, ShieldCheck, Zap } from 'lucide-react';
import { useGoogleOAuth } from '@/hooks/use-google-oauth';
import { useSessionToken } from '@/hooks/use-session-token';
import { useOAuthErrorHandling } from '@/hooks/use-oauth-error-handling';
import { AnimatedBackground } from '@/components/marketing';
import { trackLoginInitiated } from '@/lib/posthog';
import {
  PENDING_SESSION_MARKER_PARAM,
  PENDING_SESSION_MARKER_VALUE,
} from '@/lib/auth/session-marker';

/**
 * Validate that a post-login `?next=` redirect target is a same-origin relative path
 * before it's ever passed to router.push. This param comes straight from the URL query
 * string (set by protected-route.tsx, but also directly craftable by anyone sharing a
 * login link), so it must be treated as attacker-controllable input — e.g.
 * `/login?next=https://evil.com` or `/login?next=//evil.com` sent to a victim. Unlike
 * the OAuth `state`-derived tasksReturnUrl in google-callback.ts (which validates
 * relative-or-same-origin-absolute for a server-side NextResponse.redirect), this only
 * ever needs to support an in-app relative path, since router.push never needs an
 * absolute URL — so only that narrower form is accepted here.
 *
 * IMPORTANT (bypass fixed here): `router.push` resolves its string argument via the
 * WHATWG `URL` relative-resolution algorithm (`new URL(href, location.href)`)
 * internally. That algorithm normalizes backslashes to forward slashes and strips C0
 * control characters (tab/newline/CR) BEFORE deciding whether the result is a
 * network-path reference that replaces the host — and it runs AFTER any hand-written
 * prefix check like `startsWith('/')`. So a value such as `/\evil.com` or
 * `/\t/evil.com` (literal tab) passes a naive prefix check but resolves to
 * `https://evil.com/` once the router actually navigates. We defend in depth: reject
 * the known-dangerous characters outright (cheap, and covers the case even if the
 * URL-based check below were ever weakened), AND independently re-derive safety by
 * running `next` through that same `URL` parsing/normalization logic against a fixed
 * placeholder origin — if resolving `next` changes the origin, it would also hijack
 * the real origin at redirect time, so reject it. The URL-based check is the primary
 * defense since it exercises the actual normalization `router.push` relies on (so it
 * also catches bypass variants we haven't enumerated), not just today's known payloads.
 *
 * @param next - The candidate `next` query param value (or null/absent)
 * @returns true only if `next` is a same-origin relative path safe to pass to router.push
 */
function isSafeNextPath(next: string | null): next is string {
  if (!next) return false;

  // Cheap first-pass rejection before doing any URL-parse work below.
  // '//evil.com' is a protocol-relative URL (the browser resolves it against the
  // current scheme), not an in-app path, so a naive startsWith('/') check would
  // wrongly accept it.
  if (!next.startsWith('/') || next.startsWith('//')) return false;

  // Explicitly reject backslashes and any C0 control/whitespace characters (tab,
  // newline, carriage return, and other control chars below 0x20) ANYWHERE in the
  // string, not just at the start — e.g. `/foo/\evil.com` could plausibly confuse a
  // URL parser downstream even with the backslash mid-string.
  if (/[\\\x00-\x1f]/.test(next)) return false;

  // Primary defense: actually run `next` through the same WHATWG URL
  // relative-resolution algorithm that `router.push` uses internally, resolved
  // against a fixed, obviously-not-real placeholder origin. If parsing `next` as a
  // relative reference against that origin changes the origin, `next` is able to
  // hijack the host — exactly as it would against the real `location.href` — so
  // reject it. This exercises real URL-parsing/normalization behavior instead of a
  // hand-maintained blocklist, so it also catches bypass variants beyond the ones
  // already found (percent-encoding tricks, other exotic normalization behaviors).
  const PLACEHOLDER_ORIGIN = 'https://internal-placeholder.invalid';
  try {
    const resolved = new URL(next, PLACEHOLDER_ORIGIN);
    // Kept for clarity/defense-in-depth even though both hold by construction once
    // the origin check passes.
    return resolved.origin === PLACEHOLDER_ORIGIN && resolved.pathname.startsWith('/');
  } catch {
    return false;
  }
}

// Component that handles search params (must be wrapped in Suspense)
function LoginForm() {
  // Handle session token exchange from OAuth (must be first)
  const { isExchanging: isSessionTokenExchanging } = useSessionToken();

  // Handle OAuth errors (shared hook)
  useOAuthErrorHandling();

  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  // Use 'login' flow which uses prompt='select_account' instead of 'consent'
  // This avoids showing the permission screen (and sending security emails) for returning users
  // New users will be handled correctly - they just won't see the consent screen first
  const { initiate: initiateGoogleLogin, isLoading: isGoogleLoading } = useGoogleOAuth('login');

  // Check if we're in an OAuth flow (has the pending-session marker in URL — the actual
  // session payload rides in an httpOnly cookie, never in the URL)
  const [isOAuthFlow, setIsOAuthFlow] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      setIsOAuthFlow(urlParams.get(PENDING_SESSION_MARKER_PARAM) === PENDING_SESSION_MARKER_VALUE);
    }
  }, []);

  // Only show loading screen if we're actually processing OAuth (session token exchange)
  // Don't show it just because auth is loading on page reload
  const isProcessingOAuth = isSessionTokenExchanging || (authLoading && isOAuthFlow);

  // Redirect to the originally-requested page if already authenticated (after session
  // token is processed), falling back to the dashboard when there's no valid `next`.
  useEffect(() => {
    if (!isProcessingOAuth && user) {
      const next = searchParams.get('next');
      router.push(isSafeNextPath(next) ? next : '/dashboard');
    }
  }, [user, isProcessingOAuth, router, searchParams]);

  const handleGoogleLogin = () => {
    trackLoginInitiated('google');
    initiateGoogleLogin();
  };

  // Show loading only if we're actually processing OAuth (session token exchange)
  if (isProcessingOAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center marketing-bg p-4 relative overflow-hidden">
      <AnimatedBackground fixed pointerEventsNone />

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        <div className="text-center mb-8">
          <Link href="/" className="inline-block hover:opacity-80 transition-opacity">
            <h1 className="text-4xl font-bold text-foreground">Email Ferret</h1>
          </Link>
          <p className="text-muted-foreground mt-3 text-lg">Your AI-powered email guardian</p>
        </div>

        <Card className="w-full shadow-2xl backdrop-blur-xl">
          <CardHeader className="space-y-1 pb-6 text-center">
            <CardTitle className="text-2xl font-bold">Welcome</CardTitle>
            <CardDescription className="text-base">
              Sign in or create an account to continue
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full h-12 bg-white hover:bg-gray-100 text-gray-900 font-medium shadow-lg hover:shadow-xl transition-all duration-300 group rounded-xl"
              disabled={isGoogleLoading}
            >
              <svg
                className="mr-3 h-5 w-5"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {isGoogleLoading ? (
                'Connecting...'
              ) : (
                <>
                  Continue with Google
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-2">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="p-2 bg-blue-500/10 rounded-full">
                  <Mail className="h-5 w-5 text-blue-400" />
                </div>
                <span className="text-xs text-muted-foreground">Smart Filtering</span>
              </div>
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="p-2 bg-purple-500/10 rounded-full">
                  <ShieldCheck className="h-5 w-5 text-purple-400" />
                </div>
                <span className="text-xs text-muted-foreground">AI Protection</span>
              </div>
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="p-2 bg-pink-500/10 rounded-full">
                  <Zap className="h-5 w-5 text-pink-400" />
                </div>
                <span className="text-xs text-muted-foreground">Instant Setup</span>
              </div>
            </div>

            <div className="text-center text-xs text-muted-foreground pt-2 border-t border-border">
              By continuing, you agree to our{' '}
              <Link href="/terms" className="underline hover:text-foreground transition-colors">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="/privacy" className="underline hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Footer Links Section */}
        <div className="mt-8 space-y-3">
          {/* Navigation Links */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground">
            <Link href="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span className="text-border">•</span>
            <Link href="/features" className="hover:text-foreground transition-colors">
              Features
            </Link>
            <span className="text-border">•</span>
            <Link href="/pricing" className="hover:text-foreground transition-colors">
              Pricing
            </Link>
            <span className="text-border">•</span>
            <Link href="/blog" className="hover:text-foreground transition-colors">
              Blog
            </Link>
          </div>
          {/* Support & Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
            <Link href="/about" className="hover:text-foreground transition-colors">
              About
            </Link>
            <span className="text-border">•</span>
            <Link href="/contact" className="hover:text-foreground transition-colors">
              Contact
            </Link>
            <span className="text-border">•</span>
            <Link href="/security" className="hover:text-foreground transition-colors">
              Security
            </Link>
            <span className="text-border">•</span>
            <Link href="/terms" className="hover:text-foreground transition-colors">
              Terms
            </Link>
            <span className="text-border">•</span>
            <Link href="/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center marketing-bg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
